// src/portal-app/operations/ReservationForms.js
//
// Every WRITE path on the Reservations surface, in one module:
//   GuestSearch            — debounced guest lookup + select (module-private)
//   SeatSheet              — seat a reservation or a waiting-list entry
//   CreateReservationForm  — new reservation (creates the guest first when new)
//   WaitlistForm           — add a guest to the waiting list
//
// Extracted from ReservationsScreen.js (Plan 4 Task 7, extraction commit) —
// see reservationsShared.js for why the file was split and along which
// boundaries. Every payload shape, every validation branch and every testid
// on the create/seat/waitlist paths is byte-identical to the pre-split
// screen; a changed argument shape here is a live reservation booked wrong.
//
// The ONE deliberate change, made in the P4 commit and explained in full at
// GuestSearch below: GuestSearch's own dead-end "Create New Guest" branch was
// deleted. Nothing about the payloads changed with it.
//
// The UTC bug fix lives in CreateReservationForm.handleSave: scheduled_for is
// built as a plain local-time ISO string (`${date}T${time}:00`), NEVER via
// .toISOString(), which would shift an evening booking to the next business
// day outside UTC.
import React, {
    useState, useEffect, useCallback, useRef,
} from 'react';
import {
    reservationService,
    waitingListService,
    guestService,
} from '../../services/api';
import { SearchInput } from '../shell/kit';
import { GlassCard } from '../shell/LiquidGlass';
import { localDateStr } from './reservationsShared';

/* ------------------------------------------------------------------ */
/* GuestSearch — debounced guest lookup + select.                      */
/*                                                                     */
/* This used to also carry its own "Create New Guest" button and inline */
/* new-guest form. That path was a dead end: the fields it collected    */
/* were written only to GuestSearch's own state and surfaced through a  */
/* display:none <div data-name data-phone data-email> that nothing ever */
/* read, so a manager who tapped it could fill in a guest and then had  */
/* no way to save one. Worse, both parents render their OWN identically */
/* labelled "Create New Guest" button (the working one) immediately     */
/* below this component, so two identical buttons stacked and the first */
/* one — the broken one — is the one a thumb lands on. Removed: guest   */
/* creation belongs to the parent form, which already owns the payload  */
/* and calls guestService.create. Exposed by the Task 7 test rewrite,   */
/* which could not reach guestService.create through the visible button.*/
/* ------------------------------------------------------------------ */
function GuestSearch({ onSelect, t }) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const timerRef = useRef(null);

    const handleSearch = useCallback((val) => {
        setQuery(val);
        if (timerRef.current) clearTimeout(timerRef.current);
        if (!val || val.length < 2) { setResults([]); return; }
        timerRef.current = setTimeout(async () => {
            setLoading(true);
            try {
                const res = await guestService.list(val);
                setResults(res || []);
            } catch (_) {
                setResults([]);
            } finally {
                setLoading(false);
            }
        }, 300);
    }, []);

    useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

    return (
        <div>
            <SearchInput
                value={query}
                onChange={handleSearch}
                placeholder={t('guest_search', {}, 'Search guest name or phone...')}
                debounce={300}
            />
            {results.length > 0 && (
                <div className="res-guest-results">
                    {results.map((g) => (
                        <div
                            key={g.id}
                            className="res-guest-result"
                            data-testid={`guest-result-${g.id}`}
                            role="button"
                            tabIndex={0}
                            onClick={() => onSelect(g)}
                            onKeyDown={(e) => { if (e.key === 'Enter') onSelect(g); }}
                        >
                            <div className="res-guest-result-name">{g.name}</div>
                            {g.phone && <div className="res-guest-result-phone">{g.phone}</div>}
                        </div>
                    ))}
                </div>
            )}
            {loading && (
                <div style={{ padding: '8px 0', fontSize: 12, color: 'var(--c-textLight)' }}>
                    {t('search', {}, 'Searching...')}
                </div>
            )}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* SeatSheet — compact bottom sheet for seating a reservation/WL      */
/* ------------------------------------------------------------------ */
export function SeatSheet({ open, onClose, onConfirm, diningOptions, initialDiningId, t }) {
    const [tableNumber, setTableNumber] = useState('');
    const [diningOptionId, setDiningOptionId] = useState(initialDiningId || '');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            setTableNumber('');
            setDiningOptionId(initialDiningId || (diningOptions[0] ? String(diningOptions[0].id) : ''));
            setSaving(false);
            setError('');
        }
    }, [open, initialDiningId, diningOptions]);

    if (!open) return null;

    const handleConfirm = async () => {
        setSaving(true);
        setError('');
        try {
            await onConfirm({
                table_number: tableNumber || null,
                dining_option_id: diningOptionId ? Number(diningOptionId) : null,
            });
        } catch (err) {
            setError(err.message || t('action_failed', {}, 'Failed. Please try again.'));
            setSaving(false);
        }
    };

    return (
        <div
            className="res-sheet-backdrop"
            data-testid="seat-sheet-backdrop"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            role="presentation"
        >
            <GlassCard veil className="res-sheet" data-testid="seat-sheet">
                <div className="res-sheet-title">{t('seat', {}, 'Seat Guest')}</div>

                {/* Dining option */}
                {diningOptions.length > 0 && (
                    <div className="res-form-field">
                        <label className="res-form-label">{t('dining_option', {}, 'Dining Option')}</label>
                        <select
                            className="res-form-select"
                            data-testid="seat-dining-select"
                            value={diningOptionId}
                            onChange={(e) => setDiningOptionId(e.target.value)}
                        >
                            <option value="">{t('all', {}, 'Any')}</option>
                            {diningOptions.map((d) => (
                                <option key={d.id} value={String(d.id)}>{d.name}</option>
                            ))}
                        </select>
                    </div>
                )}

                {/* Table number */}
                <div className="res-form-field">
                    <label className="res-form-label">{t('table_number', {}, 'Table Number')}</label>
                    <input
                        type="text"
                        className="res-form-input"
                        data-testid="seat-table-input"
                        value={tableNumber}
                        placeholder={t('table_number', {}, 'Table number (optional)')}
                        onChange={(e) => setTableNumber(e.target.value)}
                    />
                </div>

                {error && <div className="res-form-error">{error}</div>}

                <div className="res-form-actions">
                    <button
                        type="button"
                        className="res-form-cancel-btn"
                        onClick={onClose}
                        disabled={saving}
                    >
                        {t('cancel', {}, 'Cancel')}
                    </button>
                    <button
                        type="button"
                        className="res-form-save-btn"
                        data-testid="seat-confirm-btn"
                        onClick={handleConfirm}
                        disabled={saving}
                    >
                        {saving ? t('saving', {}, 'Saving...') : t('seat', {}, 'Seat')}
                    </button>
                </div>
            </GlassCard>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* CreateReservationForm — glass bottom sheet                          */
/* ------------------------------------------------------------------ */
export function CreateReservationForm({
    open,
    onClose,
    onSaved,
    diningOptions,
    initialDate,
    t,
}) {
    const [selectedGuest, setSelectedGuest] = useState(null);
    const [isNewGuest, setIsNewGuest] = useState(false);
    const [newGuestName, setNewGuestName] = useState('');
    const [newGuestPhone, setNewGuestPhone] = useState('');
    const [newGuestEmail, setNewGuestEmail] = useState('');
    const [date, setDate] = useState(initialDate || localDateStr());
    const [time, setTime] = useState('12:00');
    const [partySize, setPartySize] = useState(2);
    const [diningOptionId, setDiningOptionId] = useState(
        diningOptions.length > 0 ? String(diningOptions[0].id) : '',
    );
    const [tableNumber, setTableNumber] = useState('');
    const [duration, setDuration] = useState('');
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            setSelectedGuest(null);
            setIsNewGuest(false);
            setNewGuestName('');
            setNewGuestPhone('');
            setNewGuestEmail('');
            setDate(initialDate || localDateStr());
            setTime('12:00');
            setPartySize(2);
            setDiningOptionId(diningOptions.length > 0 ? String(diningOptions[0].id) : '');
            setTableNumber('');
            setDuration('');
            setNotes('');
            setSaving(false);
            setError('');
        }
    }, [open, initialDate, diningOptions]);

    if (!open) return null;

    const handleSave = async () => {
        setSaving(true);
        setError('');

        try {
            let guestId;

            if (isNewGuest) {
                if (!newGuestName.trim()) {
                    setError(t('guest_name', {}, 'Guest name is required'));
                    setSaving(false);
                    return;
                }
                // Create guest first
                const newGuest = await guestService.create({
                    name: newGuestName.trim(),
                    phone: newGuestPhone.trim() || undefined,
                    email: newGuestEmail.trim() || undefined,
                });
                guestId = newGuest.id;
            } else {
                if (!selectedGuest) {
                    setError(t('guest_search', {}, 'Please select or create a guest'));
                    setSaving(false);
                    return;
                }
                guestId = selectedGuest.id;
            }

            // CRITICAL: build scheduled_for as local-time ISO string
            // NEVER use .toISOString() — that converts to UTC and shifts the
            // date for evening bookings in non-UTC timezones.
            const scheduled_for = `${date}T${time}:00`;

            const payload = {
                guest_id: guestId,
                scheduled_for,
                party_size: partySize,
                dining_option_id: diningOptionId ? Number(diningOptionId) : undefined,
                table_number: tableNumber.trim() || undefined,
                duration_estimate: duration ? Number(duration) : undefined,
                notes: notes.trim() || undefined,
            };

            await reservationService.create(payload);
            onSaved();
        } catch (err) {
            setError(err.message || t('action_failed', {}, 'Failed. Please try again.'));
            setSaving(false);
        }
    };

    return (
        <div
            className="res-sheet-backdrop"
            data-testid="create-form-backdrop"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            role="presentation"
        >
            <GlassCard veil className="res-sheet" data-testid="create-form">
                <div className="res-sheet-title">{t('add_reservation', {}, 'Add Reservation')}</div>

                {/* Guest selection */}
                <div className="res-form-field">
                    <label className="res-form-label">{t('guest_search', {}, 'Guest')}</label>
                    {selectedGuest && !isNewGuest ? (
                        <div className="res-selected-guest">
                            <div className="res-selected-guest-name">{selectedGuest.name}</div>
                            {selectedGuest.phone && (
                                <div style={{ fontSize: 11, color: 'var(--c-textLight)', marginTop: 2 }}>
                                    {selectedGuest.phone}
                                </div>
                            )}
                            <button
                                type="button"
                                className="res-selected-guest-change"
                                onClick={() => setSelectedGuest(null)}
                            >
                                {t('change_guest', {}, 'Change')}
                            </button>
                        </div>
                    ) : isNewGuest ? (
                        <div data-testid="new-guest-form">
                            <div className="res-form-field">
                                <input
                                    type="text"
                                    className="res-form-input"
                                    data-testid="new-guest-name"
                                    value={newGuestName}
                                    placeholder={t('guest_name', {}, 'Full name')}
                                    onChange={(e) => setNewGuestName(e.target.value)}
                                />
                            </div>
                            <div className="res-form-field">
                                <input
                                    type="tel"
                                    className="res-form-input"
                                    data-testid="new-guest-phone"
                                    value={newGuestPhone}
                                    placeholder={t('guest_phone', {}, 'Phone (optional)')}
                                    onChange={(e) => setNewGuestPhone(e.target.value)}
                                />
                            </div>
                            <div className="res-form-field">
                                <input
                                    type="email"
                                    className="res-form-input"
                                    data-testid="new-guest-email"
                                    value={newGuestEmail}
                                    placeholder={t('guest_email', {}, 'Email (optional)')}
                                    onChange={(e) => setNewGuestEmail(e.target.value)}
                                />
                            </div>
                            <button
                                type="button"
                                className="res-selected-guest-change"
                                onClick={() => setIsNewGuest(false)}
                            >
                                {t('back_to_search', {}, 'Back to search')}
                            </button>
                        </div>
                    ) : (
                        <GuestSearch
                            onSelect={(g) => setSelectedGuest(g)}
                            t={t}
                        />
                    )}
                    {!isNewGuest && !selectedGuest && (
                        <button
                            type="button"
                            className="res-create-guest-btn"
                            data-testid="create-new-guest-btn"
                            onClick={() => setIsNewGuest(true)}
                            style={{ marginTop: 4 }}
                        >
                            {t('create_guest', {}, 'Create New Guest')}
                        </button>
                    )}
                </div>

                {/* Date + Time */}
                <div className="res-form-row">
                    <div className="res-form-field">
                        <label className="res-form-label">{t('reservation_date', {}, 'Date')}</label>
                        <input
                            type="date"
                            className="res-form-input"
                            data-testid="form-date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                        />
                    </div>
                    <div className="res-form-field">
                        <label className="res-form-label">{t('reservation_time', {}, 'Time')}</label>
                        <input
                            type="time"
                            className="res-form-input"
                            data-testid="form-time"
                            value={time}
                            onChange={(e) => setTime(e.target.value)}
                        />
                    </div>
                </div>

                {/* Party size */}
                <div className="res-form-field">
                    <label className="res-form-label">{t('party_size', {}, 'Party Size')}</label>
                    <div className="res-stepper">
                        <button
                            type="button"
                            className="res-stepper-btn"
                            onClick={() => setPartySize((p) => Math.max(1, p - 1))}
                            aria-label={t('decrease_party_size', {}, 'Decrease party size')}
                        >−</button>
                        <input
                            type="number"
                            className="res-stepper-val"
                            data-testid="form-party-size"
                            value={partySize}
                            min="1"
                            max="20"
                            style={{ border: 'none', background: 'transparent', textAlign: 'center', width: 40 }}
                            onChange={(e) => {
                                const v = parseInt(e.target.value, 10);
                                if (!isNaN(v)) setPartySize(Math.max(1, Math.min(20, v)));
                            }}
                        />
                        <button
                            type="button"
                            className="res-stepper-btn"
                            onClick={() => setPartySize((p) => Math.min(20, p + 1))}
                            aria-label={t('increase_party_size', {}, 'Increase party size')}
                        >+</button>
                    </div>
                </div>

                {/* Dining option */}
                {diningOptions.length > 0 && (
                    <div className="res-form-field">
                        <label className="res-form-label">{t('dining_option', {}, 'Dining Option')}</label>
                        <select
                            className="res-form-select"
                            data-testid="form-dining-option"
                            value={diningOptionId}
                            onChange={(e) => setDiningOptionId(e.target.value)}
                        >
                            <option value="">{t('all', {}, 'Any')}</option>
                            {diningOptions.map((d) => (
                                <option key={d.id} value={String(d.id)}>{d.name}</option>
                            ))}
                        </select>
                    </div>
                )}

                {/* Table number */}
                <div className="res-form-field">
                    <label className="res-form-label">{t('table_number', {}, 'Table Number')}</label>
                    <input
                        type="text"
                        className="res-form-input"
                        data-testid="form-table-number"
                        value={tableNumber}
                        placeholder={t('table_number', {}, 'Table number (optional)')}
                        onChange={(e) => setTableNumber(e.target.value)}
                    />
                </div>

                {/* Duration + Notes in one row */}
                <div className="res-form-row">
                    <div className="res-form-field">
                        <label className="res-form-label">{t('duration_estimate', {}, 'Duration (min)')}</label>
                        <input
                            type="number"
                            className="res-form-input"
                            data-testid="form-duration"
                            value={duration}
                            placeholder="90"
                            min="0"
                            onChange={(e) => setDuration(e.target.value)}
                        />
                    </div>
                    <div className="res-form-field">
                        <label className="res-form-label">{t('notes', {}, 'Notes')}</label>
                        <input
                            type="text"
                            className="res-form-input"
                            data-testid="form-notes"
                            value={notes}
                            placeholder={t('notes', {}, 'Optional notes')}
                            onChange={(e) => setNotes(e.target.value)}
                        />
                    </div>
                </div>

                {error && <div className="res-form-error">{error}</div>}

                <div className="res-form-actions">
                    <button
                        type="button"
                        className="res-form-cancel-btn"
                        onClick={onClose}
                        disabled={saving}
                    >
                        {t('cancel', {}, 'Cancel')}
                    </button>
                    <button
                        type="button"
                        className="res-form-save-btn"
                        data-testid="form-save-btn"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? t('saving', {}, 'Saving...') : t('save', {}, 'Save')}
                    </button>
                </div>
            </GlassCard>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* WaitlistForm — add to waiting list                                  */
/* ------------------------------------------------------------------ */
export function WaitlistForm({ open, onClose, onSaved, diningOptions, t }) {
    const [selectedGuest, setSelectedGuest] = useState(null);
    const [isNewGuest, setIsNewGuest] = useState(false);
    const [newGuestName, setNewGuestName] = useState('');
    const [newGuestPhone, setNewGuestPhone] = useState('');
    const [partySize, setPartySize] = useState(2);
    const [diningOptionId, setDiningOptionId] = useState('');
    const [expectedWait, setExpectedWait] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            setSelectedGuest(null);
            setIsNewGuest(false);
            setNewGuestName('');
            setNewGuestPhone('');
            setPartySize(2);
            setDiningOptionId('');
            setExpectedWait('');
            setSaving(false);
            setError('');
        }
    }, [open]);

    if (!open) return null;

    const handleSave = async () => {
        setSaving(true);
        setError('');

        try {
            let guestId;

            if (isNewGuest) {
                if (!newGuestName.trim()) {
                    setError(t('guest_name', {}, 'Guest name is required'));
                    setSaving(false);
                    return;
                }
                const newGuest = await guestService.create({
                    name: newGuestName.trim(),
                    phone: newGuestPhone.trim() || undefined,
                });
                guestId = newGuest.id;
            } else {
                if (!selectedGuest) {
                    setError(t('guest_search', {}, 'Please select or create a guest'));
                    setSaving(false);
                    return;
                }
                guestId = selectedGuest.id;
            }

            await waitingListService.add({
                guest_id: guestId,
                party_size: partySize,
                dining_option_id: diningOptionId ? Number(diningOptionId) : undefined,
                expected_wait_minutes: expectedWait ? Number(expectedWait) : undefined,
            });
            onSaved();
        } catch (err) {
            setError(err.message || t('add_to_waitlist', {}, 'Failed to add to waitlist.'));
            setSaving(false);
        }
    };

    return (
        <div
            className="res-sheet-backdrop"
            data-testid="waitlist-form-backdrop"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            role="presentation"
        >
            <GlassCard veil className="res-sheet" data-testid="waitlist-form">
                <div className="res-sheet-title">{t('add_to_waitlist', {}, 'Add to Waitlist')}</div>

                {/* Guest */}
                <div className="res-form-field">
                    <label className="res-form-label">{t('guest_search', {}, 'Guest')}</label>
                    {selectedGuest && !isNewGuest ? (
                        <div className="res-selected-guest">
                            <div className="res-selected-guest-name">{selectedGuest.name}</div>
                            <button
                                type="button"
                                className="res-selected-guest-change"
                                onClick={() => setSelectedGuest(null)}
                            >
                                {t('change_guest', {}, 'Change')}
                            </button>
                        </div>
                    ) : isNewGuest ? (
                        <div>
                            <input
                                type="text"
                                className="res-form-input"
                                data-testid="new-guest-name"
                                value={newGuestName}
                                placeholder={t('guest_name', {}, 'Full name')}
                                onChange={(e) => setNewGuestName(e.target.value)}
                                style={{ marginBottom: 8 }}
                            />
                            <input
                                type="tel"
                                className="res-form-input"
                                data-testid="new-guest-phone"
                                value={newGuestPhone}
                                placeholder={t('guest_phone', {}, 'Phone (optional)')}
                                onChange={(e) => setNewGuestPhone(e.target.value)}
                            />
                            <button
                                type="button"
                                className="res-selected-guest-change"
                                onClick={() => setIsNewGuest(false)}
                                style={{ marginTop: 8 }}
                            >
                                {t('back_to_search', {}, 'Back to search')}
                            </button>
                        </div>
                    ) : (
                        <GuestSearch
                            onSelect={(g) => setSelectedGuest(g)}
                            t={t}
                        />
                    )}
                    {!isNewGuest && !selectedGuest && (
                        <button
                            type="button"
                            className="res-create-guest-btn"
                            data-testid="create-new-guest-btn"
                            onClick={() => setIsNewGuest(true)}
                            style={{ marginTop: 4 }}
                        >
                            {t('create_guest', {}, 'Create New Guest')}
                        </button>
                    )}
                </div>

                {/* Party size */}
                <div className="res-form-field">
                    <label className="res-form-label">{t('party_size', {}, 'Party Size')}</label>
                    <input
                        type="number"
                        className="res-form-input"
                        data-testid="wl-party-size"
                        value={partySize}
                        min="1"
                        max="20"
                        onChange={(e) => setPartySize(Number(e.target.value))}
                    />
                </div>

                {/* Dining option */}
                {diningOptions.length > 0 && (
                    <div className="res-form-field">
                        <label className="res-form-label">{t('dining_option', {}, 'Dining Option (optional)')}</label>
                        <select
                            className="res-form-select"
                            data-testid="wl-dining-option"
                            value={diningOptionId}
                            onChange={(e) => setDiningOptionId(e.target.value)}
                        >
                            <option value="">{t('all', {}, 'Any')}</option>
                            {diningOptions.map((d) => (
                                <option key={d.id} value={String(d.id)}>{d.name}</option>
                            ))}
                        </select>
                    </div>
                )}

                {/* Expected wait */}
                <div className="res-form-field">
                    <label className="res-form-label">{t('expected_wait', {}, 'Expected Wait (min)')}</label>
                    <input
                        type="number"
                        className="res-form-input"
                        data-testid="wl-expected-wait"
                        value={expectedWait}
                        placeholder="15"
                        min="0"
                        onChange={(e) => setExpectedWait(e.target.value)}
                    />
                </div>

                {error && <div className="res-form-error">{error}</div>}

                <div className="res-form-actions">
                    <button
                        type="button"
                        className="res-form-cancel-btn"
                        onClick={onClose}
                        disabled={saving}
                    >
                        {t('cancel', {}, 'Cancel')}
                    </button>
                    <button
                        type="button"
                        className="res-form-save-btn"
                        data-testid="wl-save-btn"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? t('saving', {}, 'Saving...') : t('save', {}, 'Save')}
                    </button>
                </div>
            </GlassCard>
        </div>
    );
}
